import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import {
  ACCOUNT_DETAIL_FONT_FAMILY,
  ACCOUNT_DETAIL_FONT_SX,
} from "../../../components/AccountDetailPolicy/accountDetailTypography";

/** Same tokens as Configuration / Account Detail. */
export const AH_ACCENT = "#F98125";
export const AH_ACCENT_HOVER = "#e8741c";
export const AH_TEXT = "#111827";
export const AH_MUTED = "#6b7280";
export const AH_SUBTLE = "#64748b";
export const AH_TRACK = "#f3f4f6";
export const AH_BORDER = "#e5e7eb";
export const AH_INPUT_BORDER = "#e2e8f0";
export const AH_WHITE = "#ffffff";

export { ACCOUNT_DETAIL_FONT_FAMILY, ACCOUNT_DETAIL_FONT_SX };

const TYPOGRAPHY_VARIANTS = [
  "h1", "h2", "h3", "h4", "h5", "h6",
  "subtitle1", "subtitle2", "body1", "body2", "button", "caption", "overline",
];

/**
 * createTheme(parentTheme, …) keeps the parent's per-variant font (Roboto), so every
 * variant is set explicitly. This also reaches portalled menus, dialogs and drawers.
 */
const interTypography = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  ...Object.fromEntries(TYPOGRAPHY_VARIANTS.map((variant) => [variant, { fontFamily: ACCOUNT_DETAIL_FONT_FAMILY }])),
};

/** Inter only — keeps the parent palette and sizes untouched. */
export const ComplianceFontProvider = ({ children }) => {
  const parentTheme = useTheme();
  const theme = useMemo(() => createTheme(parentTheme, { typography: interTypography }), [parentTheme]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export const auditHubPageSx = {
  ...ACCOUNT_DETAIL_FONT_SX,
  fontSize: 13,
  color: AH_TEXT,
  "& .MuiTypography-root, & .MuiButton-root, & .MuiTableCell-root, & .MuiInputBase-root, & .MuiChip-root, & .MuiSelect-select, & .MuiMenuItem-root, & .MuiFormLabel-root, & .MuiInputLabel-root, & .MuiAutocomplete-root":
    {
      fontFamily: "inherit",
    },
};

export const primaryButtonSx = {
  height: 40,
  minHeight: 40,
  backgroundColor: AH_ACCENT,
  color: AH_WHITE,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2,
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: AH_BORDER,
    color: "#9ca3af",
  },
};

export const outlinedButtonSx = {
  height: 40,
  minHeight: 40,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  borderColor: AH_BORDER,
  color: AH_TEXT,
  boxShadow: "none",
  "&:hover": {
    borderColor: AH_MUTED,
    backgroundColor: AH_TRACK,
    boxShadow: "none",
  },
};

export const accentOutlinedButtonSx = {
  ...outlinedButtonSx,
  color: AH_ACCENT,
  borderColor: AH_ACCENT,
  backgroundColor: AH_WHITE,
  "&:hover": {
    color: AH_ACCENT_HOVER,
    borderColor: AH_ACCENT_HOVER,
    backgroundColor: "#fff4eb",
    boxShadow: "none",
  },
};

/** Same blue outline as the control panel's Upload File / Add Link / New Task buttons. */
export const blueOutlinedButtonSx = {
  ...outlinedButtonSx,
  color: "#1976d2",
  borderColor: "#bfdbfe",
  backgroundColor: AH_WHITE,
  "&:hover": {
    borderColor: "#1976d2",
    backgroundColor: "#eff6ff",
    boxShadow: "none",
  },
};

/** Vertical rule used between peer facts in Audit Hub summary lines. */
export const MetaSeparator = ({ hideOnMobile = false }) => (
  <Box
    aria-hidden
    sx={{
      width: "1px",
      height: 18,
      bgcolor: "#9ca3af",
      flexShrink: 0,
      ...(hideOnMobile ? { display: { xs: "none", sm: "block" } } : null),
    }}
  />
);

/** "Back to Audit Hub": matches the Compliance controls page Back button. */
export const backButtonSx = {
  textTransform: "none",
  fontWeight: "bold",
  whiteSpace: "nowrap",
  flexShrink: 0,
  borderColor: "#1976d2",
  color: "#1976d2",
  "&:hover": {
    borderColor: "#1565c0",
    backgroundColor: "#e3f2fd",
  },
};

export const searchFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    borderRadius: "8px",
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: AH_TEXT,
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

export const cardShellSx = {
  border: `1px solid ${AH_BORDER}`,
  borderRadius: "12px",
  backgroundColor: AH_WHITE,
  boxSizing: "border-box",
};

/** Configuration Resource Groups / Accounts card: gray track, 12px radius. */
export const tableShellSx = {
  width: "100%",
  border: `1px solid ${AH_BORDER}`,
  borderRadius: "12px",
  p: { xs: 1.5, sm: 2.5 },
  backgroundColor: AH_TRACK,
  boxSizing: "border-box",
};

/** Search, filter, and primary action stay on one row at every breakpoint. */
export const toolbarRowSx = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: 1.25,
  width: "100%",
  minWidth: 0,
  overflowX: "auto",
  scrollbarWidth: "thin",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": { height: 6 },
};

export const AuditHubThemeProvider = ({ children }) => {
  const parentTheme = useTheme();
  const theme = useMemo(
    () =>
      createTheme(parentTheme, {
        typography: interTypography,
        palette: {
          primary: {
            main: AH_ACCENT,
            contrastText: AH_WHITE,
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                fontWeight: 600,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: { fontFamily: ACCOUNT_DETAIL_FONT_FAMILY },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                fontSize: "0.75rem",
              },
            },
          },
        },
      }),
    [parentTheme]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
